import { useId, type CSSProperties } from 'react';

import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import { formatTime } from '@/features/storefront/sections/bookingDays';
import type { BookingPaymentControls } from '@/features/storefront/sections/useBookingPayment';
import { STOREFRONT_BUTTON_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * The booking forms' payment parts, drawn from `useBookingPayment`: what is
 * due before booking (and the shopper's two answers), and what happened to
 * the payment after. Both draw nothing on a shop without booking payments.
 */

const MUTED: CSSProperties = { color: 'var(--storefront-muted)' };
const BOX: CSSProperties = {
  borderColor: 'color-mix(in srgb, var(--storefront-text) 14%, var(--storefront-background))',
  borderRadius: 'var(--storefront-radius)',
};

/** One line for the summary: "₹160 now · ₹640 at the venue", a package session, or null. */
export function paymentSummary(
  payment: BookingPaymentControls,
  copy: ReturnType<typeof useShopContent>,
): string | null {
  if (payment.usingPackage) return copy('booking.package_summary');
  if (!payment.hint) return null;
  if (payment.dueNow <= 0) return copy('booking.pay_at_venue_all');
  const parts = [`${copy('booking.pay_due_now')}: ${formatMoney(payment.dueNow)}`];
  if (payment.atVenue > 0) parts.push(copy('booking.pay_at_venue_rest', { amount: formatMoney(payment.atVenue) }));
  return parts.join(' · ');
}

/**
 * Above the button: the package toggle, the "pay now or at the venue" choice,
 * and the amounts - due now, the total, and what is left to pay there.
 */
export function BookingPaymentOptions({
  payment,
  className,
}: {
  payment: BookingPaymentControls;
  className?: string;
}) {
  const copy = useShopContent();
  const group = useId();
  const { credit, hint } = payment;
  if (!credit && !hint) return null;

  return (
    <div className={cn('grid gap-3 border p-4 text-sm', className)} style={BOX} data-booking-payment>
      {credit ? (
        <label className="flex items-start gap-2.5" data-booking-use-package>
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0"
            checked={payment.packageOn}
            onChange={(event) => payment.setPackageOn(event.target.checked)}
          />
          <span>
            <span className="block font-medium">
              {copy('booking.use_package', { count: credit.sessions_left, name: credit.package_name })}
            </span>
            {payment.usingPackage ? (
              <span className="mt-0.5 block text-xs" style={MUTED}>
                {copy('booking.use_package_hint')}
              </span>
            ) : null}
          </span>
        </label>
      ) : null}

      {payment.asksChoice ? (
        <fieldset data-booking-pay-choice>
          <legend className="mb-2 font-medium">{copy('booking.pay_choice_label')}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {([true, false] as const).map((now) => (
              <label
                key={String(now)}
                className="flex items-center gap-2 border px-3 py-2.5"
                style={{
                  ...BOX,
                  ...(payment.payNow === now ? { borderColor: 'var(--storefront-primary)' } : {}),
                }}
              >
                <input
                  type="radio"
                  name={group}
                  checked={payment.payNow === now}
                  onChange={() => payment.setPayNow(now)}
                />
                {copy(now ? 'booking.pay_now_option' : 'booking.pay_later_option')}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {hint && !payment.usingPackage ? (
        <dl className="grid gap-1.5" data-booking-amounts>
          {payment.dueNow > 0 ? (
            <div className="flex justify-between gap-3 font-semibold" data-amount="due-now">
              <dt>{copy('booking.pay_due_now')}</dt>
              <dd>{formatMoney(payment.dueNow)}</dd>
            </div>
          ) : null}
          {payment.total > 0 ? (
            <div className="flex justify-between gap-3" style={MUTED} data-amount="total">
              <dt>{copy('booking.pay_total')}</dt>
              <dd>{formatMoney(payment.total)}</dd>
            </div>
          ) : null}
          {payment.atVenue > 0 ? (
            <p className="text-xs" style={MUTED} data-amount="at-venue">
              {payment.dueNow > 0
                ? copy('booking.pay_at_venue_rest', { amount: formatMoney(payment.atVenue) })
                : copy('booking.pay_at_venue_all')}
            </p>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

/**
 * On the confirmation: what was paid, the sessions left, or - for a held slot
 * not yet paid - the time it is held until and a way to pay; once the hold has
 * run out, a way back to the times.
 */
export function BookingPaymentOutcome({
  payment,
  zone,
  onPickAnother,
}: {
  payment: BookingPaymentControls;
  /** The shop's zone, so "held until 10:15" is the shop's clock like every other time. */
  zone?: string;
  onPickAnother: () => void;
}) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();
  const { phase } = payment;

  const sessions =
    payment.sessionsLeft !== null ? (
      <p className="mt-2 text-sm" data-booking-sessions-left>
        {copy('booking.sessions_left', { count: payment.sessionsLeft })}
      </p>
    ) : null;

  if (phase.kind === 'paying') {
    return (
      <p className="mt-3 text-sm" style={MUTED} role="status" data-booking-payment-state="paying">
        {copy('booking.paying')}
      </p>
    );
  }

  if (phase.kind === 'paid') {
    return (
      <div className="mt-3" data-booking-payment-state="paid">
        <p className="text-sm font-semibold">{copy('booking.paid_amount', { amount: formatMoney(phase.amount) })}</p>
        <p className="mt-1 text-sm" style={MUTED}>
          {copy('booking.paid_note')}
        </p>
        {sessions}
      </div>
    );
  }

  if (phase.kind === 'awaiting') {
    return (
      <div className="mx-auto mt-4 max-w-md border p-4 text-sm" style={BOX} data-booking-payment-state="awaiting">
        <p className="font-semibold">{copy('booking.hold_title')}</p>
        {phase.holdExpiresAt ? (
          <p className="mt-1" style={MUTED} data-booking-hold>
            {copy('booking.hold_until', { time: formatTime(phase.holdExpiresAt, locale, zone) })}
            {payment.countdown ? (
              <span className="ms-1 font-semibold tabular-nums" style={{ color: 'var(--storefront-text)' }}>
                {copy('booking.hold_left', { time: payment.countdown })}
              </span>
            ) : null}
          </p>
        ) : null}
        {payment.error ? (
          <p className="mt-2" role="alert">
            {payment.error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => void payment.retry()}
          className="mt-3 h-11 w-full px-6 text-sm font-semibold"
          style={STOREFRONT_BUTTON_STYLE}
          data-booking-pay-retry
        >
          {Number(phase.amount) > 0
            ? `${copy('booking.pay_retry')} · ${formatMoney(phase.amount)}`
            : copy('booking.pay_retry')}
        </button>
      </div>
    );
  }

  if (phase.kind === 'expired') {
    return (
      <div className="mx-auto mt-4 max-w-md border p-4 text-sm" style={BOX} data-booking-payment-state="expired">
        <p role="alert">{copy('booking.hold_expired')}</p>
        <button
          type="button"
          onClick={onPickAnother}
          className="mt-3 h-11 w-full px-6 text-sm font-semibold"
          style={STOREFRONT_BUTTON_STYLE}
          data-booking-pick-another
        >
          {copy('booking.pick_another')}
        </button>
      </div>
    );
  }

  return sessions;
}
